import {
  Box,
  CardContent,
  CardHeader,
  List,
  ListItem,
  styled,
  Typography,
  BoxProps,
  darken,
  Link,
} from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { CheckCircle, ArrowOutward } from '@mui/icons-material';

import { formatMonetaryAmount, isDarkMode } from '@localstack/services';
import { EMULATOR_LABELS, ExternalLink } from '@localstack/constants';
import { EmulatorType } from '@localstack/types';

import { ProgressButton, ProgressButtonProps } from '../../../feedback';
import { PriceWithDiscount } from '../PriceWithDiscount';
import { EmulatorIcon } from '../EmulatorIcon';

export type PlanSummaryCardProps = {
  title: string;
  subheader: string;
  monthlyPrice?: number;
  discountedMonthlyPrice?: number;
  currency?: string;
  features: string[];
  emulator: EmulatorType;
  showExternalPurchaseLink?: boolean;
  defaultButtonColor?: boolean;
  overrideButtonText?: string;
  hidePrice?: boolean;
  onButtonClick?: () => void;
  isButtonLoading?: boolean;
  highlightedBackground?: boolean;
  billingIntervalToggle?: JSX.Element;
  fineprint?: ReactNode;
};

const FlexRowCenterBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

const SpaceBetweenBox = styled(FlexRowCenterBox)(() => ({
  justifyContent: 'space-between',
}));

const EmulatorTagIcon = styled(EmulatorIcon)(() => ({
  fontSize: 24,
  paddingRight: 4,
}));

const AWSText = styled(Typography)(({ theme }) => ({
  color: theme.palette.aws.text,
  fontWeight: 600,
}));

const SnowflakeText = styled(Typography)(({ theme }) => ({
  color: theme.palette.snowflake.text,
  fontWeight: 600,
}));

const AzureText = styled(Typography)(({ theme }) => ({
  color: theme.palette.azure.text,
  fontWeight: 600,
}));

const CheckIcon = styled(CheckCircle)(() => ({
  paddingRight: 4,
}));

const TitleText = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 24,
  lineHeight: 1.2,
}));

const ExternalPurchaseText = styled(Typography)(() => ({
  fontSize: 14,
}));

const ExternalMarketplaceLink = styled(Link)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
}));

type StyledSelectPlanButton = ProgressButtonProps & {
  selectButtonColor: 'secondary' | 'primary';
};

const SelectPlanButton = styled(ProgressButton, {
  shouldForwardProp: (prop) => prop !== 'selectButtonColor',
})<StyledSelectPlanButton>(({ theme, selectButtonColor }) => ({
  margin: '8px 0px 16px 0px',
  border: '1px solid',
  borderColor: theme.palette.divider,
  '&:hover': {
    backgroundColor: !isDarkMode(theme) ? theme.palette[selectButtonColor].main : undefined,
  },
  textTransform: 'none',
}));

type StyledPlanContainer = BoxProps & {
  highlight: boolean;
};

const PlanContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'highlight',
})<StyledPlanContainer>(({ theme, highlight }) => ({
  width: '100%',
  backgroundColor: highlight ? darken(theme.palette.background.paper, 0.05) : undefined,
}));

const formatPrice = (price: number | undefined, currency?: string): string | undefined => {
  if (price === undefined) {
    return undefined;
  }

  if (currency) return formatMonetaryAmount(price, currency);

  return `$${price}`;
};

const EMULATOR_TAG_TEXT: Record<EmulatorType, typeof AWSText> = {
  [EmulatorType.AWS]: AWSText,
  [EmulatorType.SNOWFLAKE]: SnowflakeText,
  [EmulatorType.AZURE]: AzureText,
};

const EmulatorTag = ({ emulator }: { emulator: EmulatorType }): JSX.Element => {
  const TagText = EMULATOR_TAG_TEXT[emulator];

  return (
    <FlexRowCenterBox>
      <EmulatorTagIcon emulator={emulator} />
      <TagText>{EMULATOR_LABELS[emulator].toUpperCase()}</TagText>
    </FlexRowCenterBox>
  );
};

export const PlanSummaryCard = ({
  title,
  subheader,
  monthlyPrice,
  discountedMonthlyPrice,
  currency,
  features,
  emulator,
  defaultButtonColor,
  hidePrice,
  overrideButtonText,
  onButtonClick,
  isButtonLoading,
  highlightedBackground,
  billingIntervalToggle,
  showExternalPurchaseLink,
  fineprint,
}: PlanSummaryCardProps): ReactElement => {
  const showPricingArea = monthlyPrice !== undefined || hidePrice;
  const selectButtonColor = defaultButtonColor ? 'secondary' : 'primary';
  const formattedMonthlyPrice = formatPrice(monthlyPrice, currency);
  const formattedDiscountedMonthlyPrice = formatPrice(discountedMonthlyPrice ?? monthlyPrice, currency);
  const showOriginalPrice =
    discountedMonthlyPrice !== undefined && monthlyPrice !== undefined && discountedMonthlyPrice !== monthlyPrice;

  return (
    <PlanContainer highlight={highlightedBackground ?? false}>
      <CardHeader
        title={
          <FlexRowCenterBox justifyContent="space-between">
            <TitleText>{title}</TitleText>
            {billingIntervalToggle}
          </FlexRowCenterBox>
        }
        subheader={subheader}
        subheaderTypographyProps={{
          noWrap: true,
          sx: {
            display: 'block',
            width: '250px',
            marginTop: '4px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          },
        }}
      />
      <CardContent>
        {showPricingArea && (
          <SpaceBetweenBox>
            <EmulatorTag emulator={emulator} />
            {!hidePrice && (
              <PriceWithDiscount
                discountedPrice={formattedDiscountedMonthlyPrice ?? ''}
                price={showOriginalPrice ? formattedMonthlyPrice : undefined}
                discountedPriceProps={{ fontWeight: 'bold' }}
                priceProps={{ color: 'text.secondary', variant: 'body2' }}
                suffix={<Typography>/mo</Typography>}
              />
            )}
          </SpaceBetweenBox>
        )}
        {onButtonClick && (
          <SelectPlanButton
            fullWidth
            variant="contained"
            color={selectButtonColor}
            disableElevation
            onClick={onButtonClick}
            loading={isButtonLoading}
            disabled={isButtonLoading}
            selectButtonColor={selectButtonColor}
          >
            {overrideButtonText ?? 'Continue to Purchase'}
          </SelectPlanButton>
        )}
        <List subheader={<Typography fontWeight={600}>Key features:</Typography>}>
          {features.map((feature) => (
            <ListItem key={feature} style={{ paddingLeft: 0 }}>
              <FlexRowCenterBox>
                <CheckIcon fontSize="small" color="disabled" />
                <Typography
                  noWrap
                  sx={{
                    display: 'block',
                    width: '100%',
                  }}
                >
                  {feature}
                </Typography>
              </FlexRowCenterBox>
            </ListItem>
          ))}
        </List>
        {showExternalPurchaseLink && (
          <Box mt={1}>
            <ExternalPurchaseText>
              Purchase a Monthly Subscription via{' '}
              <ExternalMarketplaceLink target="__blank" href={ExternalLink.EXTERNAL_AWS_PRODUCT_LINK} underline="hover">
                AWS Marketplace <ArrowOutward fontSize="small" />
              </ExternalMarketplaceLink>
            </ExternalPurchaseText>
          </Box>
        )}
        {fineprint}
      </CardContent>
    </PlanContainer>
  );
};
